import { Observable, BehaviorSubject, of as observableOf, of } from 'rxjs';
import { mergeMap, catchError, share, filter } from "rxjs/operators";
import { addProvider, IProviderDescriptor } from "../ServiceUtil";
import { ITimeCard, ITimeSheet, ITimeSheetDetail, ITimeSheetRequestData, ITimeSheetResponse } from "./invoice.interface";

import { AuthenticationService } from "../AuthenticationService";
import { InvoiceDelegate } from "./invoice.delegate";
import { IBaseResponse } from '../ClientService';
import { ITimesheetInternal } from "./timesheet.dto";

export class InvoiceService
{
    private empTimeCard: ITimeCard = null;

    private timeSheetsData: ITimeSheet[] = null;
    private timeSheetsSubject: BehaviorSubject<ITimeSheet[]> = new BehaviorSubject(null);
    public timeSheets: Observable<ITimeSheet[]> = this.timeSheetsSubject.pipe(filter((data) => !!data));


    private externalTimeSheetsData: ITimeSheet[] = null;
    private externalTimeSheetsSubject: BehaviorSubject<ITimeSheet[]> = new BehaviorSubject(null);
    public externalTimeSheets: Observable<ITimeSheet[]> = this.externalTimeSheetsSubject.pipe(filter((data) => !!data));

    private selectedProjectCodeId: string = '0';

    private selectedTimeSheetId: string = '0';

    private lastTimeSheetSubmitDate: Date = null;
    private lastTimesheetSubmitDateSubject: BehaviorSubject<Date> = new BehaviorSubject(null);
    public lastSubmitTimeSheetDate: Observable<Date> = this.lastTimesheetSubmitDateSubject.pipe(filter((data) => !!data));

    private selectedTimeSheetData: ITimesheetInternal = null;
    private selectedTimeSheetSubject: BehaviorSubject<ITimesheetInternal> = new BehaviorSubject(this.selectedTimeSheetData);
    public selectedTimeSheet: Observable<ITimesheetInternal> = this.selectedTimeSheetSubject;

    private managerTimeSheetsData: ITimeSheet[] = null;
    private managerTimeSheetsSubject: BehaviorSubject<ITimeSheet[]> = new BehaviorSubject(null);
    public managerTimeSheets: Observable<ITimeSheet[]> = this.managerTimeSheetsSubject.pipe(filter((data) => !!data));


    constructor(private invoiceDelegate: InvoiceDelegate, private authenticationService: AuthenticationService) { }

    public get TimeSheetId(): string
    {
        return this.selectedTimeSheetId;
    }

    public getSelectedTimeSheetData(): ITimeSheet
    {
        return this.externalTimeSheetsData.find((timeSheet: ITimeSheet) =>
            timeSheet.timeSheetId ===
            this.selectedTimeSheetId);
    }

    public setSelectedTimeSheetId(timeSheetId: string): void
    {
        this.selectedTimeSheetId = timeSheetId;
    }

    public get ProjectCodeId(): string
    {
        return this.selectedProjectCodeId;
    }

    public setSelectedProjectCodeId(projectCodeId: string): void
    {
        this.selectedProjectCodeId = projectCodeId;
    }

    public createNewEmployeeTimeCard(empTimecard: ITimeCard): Observable<string>
    {

        var response = this.invoiceDelegate.createNewEmployeeTimeCard({ ...empTimecard, ...this.authenticationService.getBaseRequest });

        return response.pipe(mergeMap((response: any) =>
        {
            if (!response)
            {
                this.lastTimeSheetSubmitDate = response.lastTimeSheetSubmitDate;
                this.lastTimesheetSubmitDateSubject.next(this.lastTimeSheetSubmitDate);
                return observableOf(response);
            }
            return observableOf(response);

        }),
            catchError((error: any) =>
            {
                // InvoiceService.logger.error(`createNewEmployeeTimeCard Fault - Error (${JSON.stringify(error)})`);
                return observableOf(error);
            }),
            share());

    }

    public getTimeSheets(fromDate: string, toDate: string, timesheetStatus: string, employeeId?: string): Observable<IBaseResponse>
    {
        var baseRequest = this.authenticationService.getBaseRequest;
        const response = this.invoiceDelegate.getAllTimeSheets(baseRequest.orgId, fromDate, toDate, timesheetStatus, employeeId);
        return this.processTimeSheets(response);
    }

    public getTimeSheetData(): ITimeSheet
    {
        return this.timeSheetsData.find((timeSheet: ITimeSheet) =>
            timeSheet.timeSheetId ===
            this.selectedTimeSheetId);
    }

    public getTimeSheetsByProjectId(projectCodeId: string): Observable<IBaseResponse>
    {
        const response = this.invoiceDelegate.getAllTimeSheetsByProjectId(projectCodeId);
        return this.processTimeSheets(response);
    }

    public getTimeSheetsByTimesheetId(timeSheetId: string): Observable<{ timeSheet: ITimesheetInternal; success: boolean }>
    {
        if (timeSheetId === "0")
        {
            this.selectedTimeSheetData = null;
            this.selectedTimeSheetSubject.next(this.selectedTimeSheetData);
            return observableOf({ timeSheet: null, success: false });
        }
        const response = this.invoiceDelegate.getAllTimeSheetByTimeSheetId(timeSheetId);
        return this.processTimeSheet(response);
    }

    public getAllClientTimeSheetsByExternalEmail(): Observable<IBaseResponse>
    {
        var baseRequest = this.authenticationService.getBaseRequest;

        const response = this.invoiceDelegate.getAllClientTimeSheetsByExternalEmail(baseRequest.userName);
        return this.processExternalTimeSheets(response);
    }

    public rejectTimeSheets(timeSheets: ITimeSheet[]): Observable<IBaseResponse>
    {

        var baseRequest = this.authenticationService.getBaseRequest;
        const timeSheetRequests: ITimeSheetRequestData[] = timeSheets.map(item =>
        {
            return {
                timeSheetId: item.timeSheetId,
                projectCodeId: item.projectCodeId,
                fromDate: item.fromDate,
                toDate: item.toDate,
                timesheetStatus: item.timeSheetStatus,
                selected: item.selected
            } as any as ITimeSheetRequestData;
        });
        const response = this.invoiceDelegate.rejectTimeSheets(timeSheetRequests, baseRequest.clientTimeStamp, baseRequest.userName, baseRequest.userId, baseRequest.orgId);
        return this.processExternalTimeSheets(response);
    }

    public approveTimeSheets(timeSheets: ITimeSheet[]): Observable<IBaseResponse>
    {

        var baseRequest = this.authenticationService.getBaseRequest;
        const timeSheetRequests: ITimeSheetRequestData[] = timeSheets.map(item =>
        {
            return {
                timeSheetId: item.timeSheetId,
                projectCodeId: item.projectCodeId,
                fromDate: item.fromDate,
                toDate: item.toDate,
                timesheetStatus: item.timeSheetStatus,
                selected: item.selected
            } as any as ITimeSheetRequestData;
        });
        const response = this.invoiceDelegate.approveTimeSheets(timeSheetRequests, baseRequest.clientTimeStamp, baseRequest.userName, baseRequest.userId, baseRequest.orgId);
        return this.processExternalTimeSheets(response);
    }

    public approveTimeSheet(timeSheetId: string, comments: string): Observable<IBaseResponse>
    {
        var baseRequest = this.authenticationService.getBaseRequest;

        const response = this.invoiceDelegate.approveTimeSheet(timeSheetId, baseRequest.clientTimeStamp, baseRequest.userName, baseRequest.userId, comments);
        return this.processExternalTimeSheets(response);
    }

    public rejectTimeSheet(timeSheetId: string, comments: string): Observable<IBaseResponse>
    {
        var baseRequest = this.authenticationService.getBaseRequest;

        const response = this.invoiceDelegate.rejectTimeSheet(timeSheetId, baseRequest.clientTimeStamp, baseRequest.userName, baseRequest.userId, comments);
        return this.processExternalTimeSheets(response);
    }

    public updateEmployeeTimeCard(empTimecard: ITimeCard): Observable<any>
    {

        var response = this.invoiceDelegate.updateNewEmployeeTimeCard({ ...empTimecard, ...this.authenticationService.getBaseRequest });

        return response.pipe(mergeMap((response: any) =>
        {
            if (!response)
            {
                return observableOf(response);
            }
            return observableOf(response);

        }),
            catchError((error: any) =>
            {
                // InvoiceService.logger.error(`createNewEmployeeTimeCard Fault - Error (${JSON.stringify(error)})`);
                return observableOf(error);
            }),
            share());

    }

    public getTimeSheetsbyAccountManagerId(year:number, timesheetStatus: string): Observable<IBaseResponse>
    {
        var accountManagerId=  this.authenticationService.GetAccountManagerId();
        const response = this.invoiceDelegate.getAllTimeSheetsByAccountManagerId(accountManagerId,year, timesheetStatus);
        return this.processManagerTimeSheets(response);
    }

    private processManagerTimeSheets(timeSheets: Observable<ITimeSheetResponse>): Observable<IBaseResponse>
    {
        timeSheets.subscribe(onTimeSheetSuccess.bind(this), onTimeSheetFault.bind(this));
 
        return timeSheets.pipe(mergeMap((response: ITimeSheetResponse) => observableOf({
            success: response.success,
            errorString: response.errorString
 
        })),
            catchError((response: ITimeSheetResponse) =>
            {
                return of({
                    success: response.success,
                    errorString: response.errorString
                });
            }),
            share());
 
        function onTimeSheetSuccess(clientResponse: ITimeSheetResponse)
        {
            if (clientResponse.success)
            {
                this.managerTimeSheetsData = clientResponse.timeSheets;
                this.managerTimeSheetsSubject.next(this.managerTimeSheetsData);
 
            }
        }
 
        function onTimeSheetFault(error: any)
        {
            //ClientService.logger.error(`processClients Fault - Error (${JSON.stringify(error)})`);
        }
    }
 

    private processExternalTimeSheets(timeSheets: Observable<ITimeSheetResponse>): Observable<IBaseResponse>
    {
        timeSheets.subscribe(onTimeSheetSuccess.bind(this), onTimeSheetFault.bind(this));

        return timeSheets.pipe(mergeMap((response: ITimeSheetResponse) => observableOf({
            success: response.success,
            errorString: response.errorString

        })),
            catchError((response: ITimeSheetResponse) =>
            {
                return of({
                    success: response.success,
                    errorString: response.errorString
                });
            }),
            share());

        function onTimeSheetSuccess(clientResponse: ITimeSheetResponse)
        {
            if (clientResponse.success)
            {
                //this.selectedClientId = clientResponse.clientId ? clientResponse.clientId : this.selectedClientId;
                this.externalTimeSheetsData = clientResponse.timeSheets;
                this.externalTimeSheetsSubject.next(this.externalTimeSheetsData);
                //this.timeSheetsFilterSubject.next(this.timeSheetsData);

            }
        }

        function onTimeSheetFault(error: any)
        {
            //ClientService.logger.error(`processClients Fault - Error (${JSON.stringify(error)})`);
        }
    }

    private processTimeSheets(timeSheets: Observable<ITimeSheetResponse>): Observable<IBaseResponse>
    {
        timeSheets.subscribe(onTimeSheetSuccess.bind(this), onTimeSheetFault.bind(this));

        return timeSheets.pipe(mergeMap((response: ITimeSheetResponse) => observableOf({
            success: response.success,
            errorString: response.errorString

        })),
            catchError((response: ITimeSheetResponse) =>
            {
                return of({
                    success: response.success,
                    errorString: response.errorString
                });
            }),
            share());

        function onTimeSheetSuccess(clientResponse: ITimeSheetResponse)
        {
            if (clientResponse.success)
            {
                //this.selectedClientId = clientResponse.clientId ? clientResponse.clientId : this.selectedClientId;
                this.timeSheetsData = clientResponse.timeSheets;
                this.timeSheetsSubject.next(this.timeSheetsData);

                this.lastTimeSheetSubmitDate = clientResponse.lastTimeSheetSubmitDate;
                this.lastTimesheetSubmitDateSubject.next(this.lastTimeSheetSubmitDate);
                //this.timeSheetsFilterSubject.next(this.timeSheetsData);

            }
        }

        function onTimeSheetFault(error: any)
        {
            //ClientService.logger.error(`processClients Fault - Error (${JSON.stringify(error)})`);
        }
    }

    private processTimeSheet(timeSheets: Observable<{ timeSheet: ITimesheetInternal; success: boolean }>): Observable<{ timeSheet: ITimesheetInternal; success: boolean }>
    {
        timeSheets.subscribe(onTimeSheetSuccess.bind(this), onTimeSheetFault.bind(this));

        return timeSheets.pipe(
            mergeMap((response: any) => observableOf(response)),
            catchError(() => observableOf({ timeSheet: null, success: false })),
            share()
        );

        function onTimeSheetSuccess(clientResponse: { timeSheet: ITimesheetInternal; success: boolean })
        {
            if (clientResponse.success && clientResponse.timeSheet)
            {
                this.selectedTimeSheetData = clientResponse.timeSheet;
                this.selectedTimeSheetSubject.next(this.selectedTimeSheetData);
            }
        }

        function onTimeSheetFault(error: any)
        {
            //ClientService.logger.error(`processClients Fault - Error (${JSON.stringify(error)})`);
        }
    }

    // Timesheet filter state management
    private timesheetFilters: {
        selectedEmp?: string;
        timesheetStatus?: string;
        selectedFromDate?: Date;
        selectedToDate?: Date;
    } = null;

    public saveTimesheetFilters(filters: {
        selectedEmp?: string;
        timesheetStatus?: string;
        selectedFromDate?: Date;
        selectedToDate?: Date;
    }): void
    {
        this.timesheetFilters = filters;
    }

    public getTimesheetFilters(): {
        selectedEmp?: string;
        timesheetStatus?: string;
        selectedFromDate?: Date;
        selectedToDate?: Date;
    }
    {
        return this.timesheetFilters;
    }

    public clearTimesheetFilters(): void
    {
        this.timesheetFilters = null;
    }

}